import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface UatRow {
  readonly uatPlanId: string;
  readonly uatCode: string;
  readonly title: string;
  readonly status: string;
  readonly goLiveRecommendation: string;
}

const STATUSES = ['', 'PLANNED', 'IN_PROGRESS', 'COMPLETED', 'SIGNED_OFF', 'REJECTED'];
const DECISIONS = ['GO', 'CONDITIONAL_GO', 'NO_GO'];

/**
 * M34 — UAT Dashboard (§31 / §18). Register of UAT plans (T456) across the
 * planned→in-progress→completed→signed-off lifecycle with the go-live
 * recommendation. Supports planning a UAT cycle and driving it — start, complete,
 * sign-off and delete — each wired to the guarded /v1/qms/uat write API (sign-off
 * carries the segregated UAT_APPROVE permission, §30 SoD). Filterable by status.
 */
export function UatDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<UatRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: UatRow[] }>(`/v1/qms/uat${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m34.uat.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/uat`, {
        method: 'POST',
        body: { title: newTitle.trim() },
        token,
      });
      setNotice(t('m34.uat.created', { title: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_UAT_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: UatRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/uat/${r.uatPlanId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m34.uat.notice', { code: r.uatCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  const remove = async (r: UatRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/uat/${r.uatPlanId}`, { method: 'DELETE', token });
      setNotice(t('m34.uat.notice', { code: r.uatCode, label: t('m34.uat.act.deleted') }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_UAT_MANAGE'));
    }
  };

  return (
    <main aria-label="uat-dashboard">
      <h1>{t('m34.uat.title')}</h1>

      <section aria-label="create-uat">
        <h2>{t('m34.uat.planHeading')}</h2>
        <form aria-label="uat-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.common.title')}<input aria-label="field-uat-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-uat" disabled={busy}>{busy ? t('m34.uat.creating') : t('m34.uat.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="uat-action-error">{actionError}</p>}
        {notice && <p aria-label="uat-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="uat-count">{t('m34.uat.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="uat-table"
            rows={rows}
            rowKey={(r) => r.uatPlanId}
            columns={[
              { key: 'uatCode', header: t('m34.common.code'), render: (r) => r.uatCode },
              { key: 'title', header: t('m34.common.title'), render: (r) => r.title },
              { key: 'status', header: t('m34.common.status'), render: (r) => r.status },
              { key: 'goLiveRecommendation', header: t('m34.uat.recommendation'), render: (r) => r.goLiveRecommendation },
              { key: 'actions', header: t('m34.common.actions'), render: (r) => (
                <>
                  {r.status === 'PLANNED' && (
                    <Button variant="secondary" type="button" aria-label={`start-${r.uatCode}`} onClick={() => void act(r, 'start', t('m34.uat.act.started'), 'M34_UAT_MANAGE')}>{t('m34.uat.btnStart')}</Button>
                  )}
                  {r.status === 'IN_PROGRESS' && (
                    <Button variant="secondary" type="button" aria-label={`complete-${r.uatCode}`} onClick={() => void act(r, 'complete', t('m34.uat.act.completed'), 'M34_UAT_MANAGE')}>{t('m34.uat.btnComplete')}</Button>
                  )}
                  {r.status === 'COMPLETED' && (
                    <>
                      <select aria-label={`decision-${r.uatCode}`} value={decisions[r.uatPlanId] ?? 'GO'} onChange={(e) => setDecisions((m) => ({ ...m, [r.uatPlanId]: e.target.value }))}>
                        {DECISIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <Button variant="secondary" type="button" aria-label={`signoff-${r.uatCode}`} onClick={() => void act(r, 'signoff', t('m34.uat.act.signedOff'), 'M34_UAT_APPROVE', { decision: decisions[r.uatPlanId] ?? 'GO' })}>{t('m34.uat.btnSignOff')}</Button>
                    </>
                  )}
                  {!['SIGNED_OFF', 'REJECTED'].includes(r.status) && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.uatCode}`} onClick={() => void remove(r)}>{t('m34.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
